import { useState, useEffect } from 'react';
import { Card, CardContent, Grid, Typography, Button, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { addressSchema } from '../../validations';
import { AddressInputs } from '../address';
import { changeAddressBtn, internalCard, purpleText } from '../../styles';

interface Props {
  mainAddress: AddressInputs;
  updateBillingAddress?: (data: AddressInputs) => Promise<void>;
  onBillingAddressChange?: (data: AddressInputs) => void;
}

const fieldLabels: Record<keyof AddressInputs, string> = {
  zipcode: 'CEP',
  street: 'Endereço',
  number: 'Número',
  additional_details: 'Complemento',
  neighborhood: 'Bairro',
  city: 'Cidade',
  state: 'Estado'
};

export function BillingAddressCard({ mainAddress, updateBillingAddress, onBillingAddressChange }: Props) {
  const [useSameAddress, setUseSameAddress] = useState(true);
  const [editMode, setEditMode] = useState(false);

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    getValues,
    formState: { errors }
  } = useForm<AddressInputs>({
    defaultValues: mainAddress,
    resolver: yupResolver(addressSchema)
  });

  useEffect(() => {
    if (useSameAddress) {
      Object.entries(mainAddress).forEach(([key, value]) => {
        setValue(key as keyof AddressInputs, value, { shouldDirty: false });
      });
      setEditMode(false);
      if (onBillingAddressChange) {
        onBillingAddressChange(mainAddress);
      }
    }
  }, [useSameAddress, mainAddress, setValue, onBillingAddressChange]);

  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (useSameAddress && name) {
        const current = values[name as keyof AddressInputs];
        const original = mainAddress[name as keyof AddressInputs];
        if (current !== original) {
          setUseSameAddress(false);
        }
      }

      if (!useSameAddress && onBillingAddressChange) {
        onBillingAddressChange(values as AddressInputs);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, useSameAddress, mainAddress, onBillingAddressChange]);

  const onSubmit: SubmitHandler<AddressInputs> = async (data) => {
    if (updateBillingAddress) {
      await updateBillingAddress(data);
    }
    setEditMode(false);
  };

  return (
    <Card sx={{ margin: { xs: '2rem 0.5rem', sm: '2rem 1rem', md: '2rem 2rem' } }}>
      <CardContent sx={internalCard}>
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          4 - Endereço de cobrança
        </Typography>

        <FormControlLabel
          control={<Checkbox checked={useSameAddress} onChange={() => setUseSameAddress((prev) => !prev)} />}
          label='Utilizar as mesmas informações do "Endereço completo (pernoite)"'
        />

        {!useSameAddress && !editMode ? (
          <>
            <Grid container spacing={2} sx={{ marginTop: 1 }}>
              {(Object.keys(fieldLabels) as (keyof AddressInputs)[]).map((fieldName) => (
                <Grid item xs={12} sm={6} lg={3} key={fieldName}>
                  <Typography variant="subtitle1" sx={purpleText}>
                    {fieldLabels[fieldName]}:
                  </Typography>
                  <Typography variant="body2">{getValues(fieldName) || '-'}</Typography>
                </Grid>
              ))}
            </Grid>
            <Button sx={changeAddressBtn} onClick={() => setEditMode(true)}>
              Alterar
            </Button>
          </>
        ) : !useSameAddress && editMode ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              {(Object.keys(fieldLabels) as (keyof AddressInputs)[]).map((fieldName) => (
                <Grid item xs={12} sm={6} lg={3} key={fieldName}>
                  <Typography sx={purpleText} variant="subtitle1">
                    {fieldLabels[fieldName]}:
                  </Typography>
                  <Controller
                    name={fieldName}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        error={!!errors?.[fieldName]}
                        helperText={errors?.[fieldName]?.message}
                        inputProps={{
                          style: {
                            padding: '8px 12px',
                            fontSize: '14px'
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
              ))}
            </Grid>
            <Button type="submit" sx={changeAddressBtn}>
              Confirmar
            </Button>
          </form>
        ) : (
          <Grid container spacing={2} sx={{ marginTop: 1 }}>
            {(Object.keys(fieldLabels) as (keyof AddressInputs)[]).map((fieldName) => (
              <Grid item xs={12} sm={6} lg={3} key={fieldName}>
                <Typography variant="subtitle1" sx={purpleText}>
                  {fieldLabels[fieldName]}:
                </Typography>
                <Typography variant="body2">{mainAddress[fieldName] || '-'}</Typography>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}
